/** Every address the portal answers.
 *
 * The routes are the product's shape: three public pages anyone may open
 * (the map, a spectate stream, the sign-in page), four that are about your
 * own driving and your own installations, and ten that are the owner's.
 * Gating lives in the route table rather than inside the screens, so the
 * answer to "who can see this" is read in one place. An admin route answers
 * a signed-out visitor 404, because a locked door still tells you there is a
 * room; an account that is signed in and is not an admin gets the 403 and the
 * reason (`RequireAdmin`).
 *
 * `/` has no page of its own. It sends a signed-in driver to their sessions,
 * which is what they came for, and everyone else to the public map, which is
 * the best argument for signing in.
 *
 * `/s/:token` is a session somebody shared by link, and is public on purpose:
 * the link is the credential, and the people it is sent to are mostly not
 * signed in. It is the Analysis page in its read-only mode, fetched only
 * when a link is opened, like the page itself.
 *
 * `/live` with no username is a deliberate addition to the map in the build
 * contract: the signed-out header offers "Spectate", and that link has to go
 * somewhere. The Live screen is rendered with `username` undefined and should
 * list who is streaming now (`listLive()`) instead of drawing one stream.
 */

import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Shell } from '@/components/Shell';
import { NotFound } from '@/components/ui/NotFound';
import { LoadingPanel, Page } from '@/components/ui';
import { RequireAdmin, RequireUser, useMe } from '@/lib/session';

import SignIn from '@/pages/SignIn';
import Tracks from '@/pages/Tracks';
import Track from '@/pages/Track';
import Sessions from '@/pages/Sessions';
import Live from '@/pages/Live';
import ProfileAccount from '@/pages/ProfileAccount';
import ProfileTokens from '@/pages/ProfileTokens';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminIssues from '@/pages/AdminIssues';
import AdminMerge from '@/pages/AdminMerge';
import AdminUploads from '@/pages/AdminUploads';
import AdminUsers from '@/pages/AdminUsers';
import AdminSessions from '@/pages/AdminSessions';
import AdminLive from '@/pages/AdminLive';
import AdminLogs from '@/pages/AdminLogs';
import AdminSettings from '@/pages/AdminSettings';
import AdminCosts from '@/pages/AdminCosts';
import AdminTrackEditor from '@/pages/AdminTrackEditor';
import AdminTracks from '@/pages/AdminTracks';
import AdminTrackHistory from '@/pages/AdminTrackHistory';

/** The Analysis page is the portal's heaviest screen — the map, the channel
 *  stack, the rack — and most visits never open it, so it is fetched when it
 *  is, rather than added to every visitor's first download. */
const Analysis = lazy(() => import('@/pages/Analysis'));
const SharedSession = lazy(() => import('@/pages/SharedSession'));

/** Where `/` goes. Nothing is rendered until the account is known, so a
 *  signed-in visitor is never bounced to the public page and back. */
function Index() {
  const { me, loading } = useMe();
  if (loading) return null;
  return <Navigate to={me ? '/sessions' : '/tracks'} replace />;
}

export function App() {
  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Index />} />

        <Route path="signin" element={<SignIn />} />
        <Route path="tracks" element={<Tracks />} />
        <Route path="tracks/:officialId" element={<Track />} />
        <Route path="live" element={<Live />} />
        <Route path="live/:username" element={<Live />} />
        <Route
          path="s/:token"
          element={
            <Suspense
              fallback={
                <Page max={1440}>
                  <LoadingPanel label="opening the shared session" />
                </Page>
              }
            >
              <SharedSession />
            </Suspense>
          }
        />

        <Route
          path="sessions"
          element={
            <RequireUser>
              <Sessions />
            </RequireUser>
          }
        />
        <Route
          path="analysis"
          element={
            <RequireUser>
              <Suspense
                fallback={
                  <Page max={1440}>
                    <LoadingPanel label="loading the analysis" />
                  </Page>
                }
              >
                <Analysis />
              </Suspense>
            </RequireUser>
          }
        />
        <Route
          path="profile"
          element={
            <RequireUser>
              <ProfileAccount />
            </RequireUser>
          }
        />
        <Route
          path="profile/tokens"
          element={
            <RequireUser>
              <ProfileTokens />
            </RequireUser>
          }
        />

        <Route path="admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route
          path="admin/dashboard"
          element={
            <RequireAdmin>
              <AdminDashboard />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/issues"
          element={
            <RequireAdmin>
              <AdminIssues />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/merge"
          element={
            <RequireAdmin>
              <AdminMerge />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/uploads"
          element={
            <RequireAdmin>
              <AdminUploads />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/users"
          element={
            <RequireAdmin>
              <AdminUsers />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/sessions"
          element={
            <RequireAdmin>
              <AdminSessions />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/live"
          element={
            <RequireAdmin>
              <AdminLive />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/logs"
          element={
            <RequireAdmin>
              <AdminLogs />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/settings"
          element={
            <RequireAdmin>
              <AdminSettings />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/tracks"
          element={
            <RequireAdmin>
              <AdminTracks />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/tracks/:officialId"
          element={
            <RequireAdmin>
              <AdminTrackHistory />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/tracks/:officialId/edit"
          element={
            <RequireAdmin>
              <AdminTrackEditor />
            </RequireAdmin>
          }
        />
        <Route
          path="admin/costs"
          element={
            <RequireAdmin>
              <AdminCosts />
            </RequireAdmin>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
